import React, { useMemo, useState, useEffect } from 'react';
import { arr, getLoginUrlWithRedirect } from '@/features/shared/lib/utils';

function fmtPercent(v) {
  if (v === null || v === undefined || isNaN(Number(v))) return '';
  const num = Number(v);
  return `${num.toFixed(2)}%`;
}

function fmtCount(v) {
  if (v === null || v === undefined) return '';

  // 숫자면 그대로, 문자열이면 콤마 제거 후 숫자로 변환 시도
  const n = (typeof v === 'number')
    ? v
    : Number(String(v).replace(/,/g, ''));

  // 숫자로 해석 가능하면 천단위 콤마 적용
  if (Number.isFinite(n)) return n.toLocaleString();

  return String(v);
}

function categoryLabel(type, edition, name) {
  if (String(type).toLowerCase() === 'jcr') {
    const ed = (edition || '').trim();
    if (ed) return `(${ed}) ${name || ''}`;
    return name || '';
  }
  return name || '';
}

export default function CategoryRankingTable({ rows }) {
  const [activeTab, setActiveTab] = useState('');

  const list = useMemo(() => {
    // 데이터 구조 변환: journal_ranking 형식을 기존 형식으로 매핑
    return arr(rows).map(r => {
      if (!r) return null;
      // 이미 기존 형식인 경우 그대로 사용
      if (r.indicator_type || r.category_name) {
        return r;
      }
      // 새로운 journal_ranking 형식인 경우 변환
      return {
        indicator_type: r.name?.toLowerCase() || '',
        indicator_year: r.year || null,
        edition: r.edition || null,
        category_name: r.ranking_type || null,
        category_count: r.category_count || null,
        category_count2: r.category_count2 || null,
        ranking: r.ranking || null,
        ranking2: r.ranking2 || null,
        percent: r.percent || null,
        percent2: r.percent2 || null,
        percentile: r.percentile || null,
        quartile: r.quartile || null,
      };
    }).filter(Boolean);
  }, [rows]);

  const tabs = useMemo(() => {
    const orderMap = { jcr: 0, scopus: 1, kci: 2 };
    const types = Array.from(new Set(list.map(r => String(r.indicator_type).toLowerCase()).filter(Boolean)));
    types.sort((a, b) => (orderMap[a] ?? 99) - (orderMap[b] ?? 99));
    return types;
  }, [list]);

  useEffect(() => {
    if (tabs.length > 0 && (!activeTab || !tabs.includes(activeTab))) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  const filtered = useMemo(() => {
    if (!activeTab) return list;
    return list.filter(r => String(r.indicator_type).toLowerCase() === activeTab);
  }, [list, activeTab]);

  const sorted = useMemo(() => {
    const orderMap = { jcr: 0, scopus: 1, kci: 2 };
    return [...filtered]
      .sort((a, b) => {
        const ya = Number(a?.indicator_year || 0);
        const yb = Number(b?.indicator_year || 0);
        if (yb !== ya) return yb - ya; // year desc
        const ta = orderMap[String(a?.indicator_type || '').toLowerCase()] ?? 99;
        const tb = orderMap[String(b?.indicator_type || '').toLowerCase()] ?? 99;
        if (ta !== tb) return ta - tb; // type order
        const ra = Number(a?.ranking || 0);
        const rb = Number(b?.ranking || 0);
        if (ra !== rb) return ra - rb; // better rank first
        const catA = a?.category_name || a?.ranking_type || '';
        const catB = b?.category_name || b?.ranking_type || '';
        return String(catA).localeCompare(String(catB));
      });
  }, [filtered]);

  if (!list.length) {
    return <div className="text-[14px] text-[#6C7687]">No category ranking data.</div>;
  }

  const renderMainAndAlt = (main, alt) => {
    const hasAlt = alt !== undefined && alt !== null && Number(alt) > 0;
    return (
      <>
        <span>{fmtCount(main)}</span>
        {hasAlt && (
          <>
            <br /><span>({fmtCount(alt)})*</span>
          </>
        )}
      </>
    );
  };

  const renderPercentWithAlt = (main, alt) => {
    const hasAlt = alt !== undefined && alt !== null && Number(alt) > 0;
    return (
      <>
        <span>{fmtPercent(main)}</span>
        {hasAlt && (
          <>
            <br /><span>({fmtPercent(alt)})*</span>
          </>
        )}
      </>
    );
  };

  const typeLabel = (t) => {
    const s = String(t || '').toLowerCase();
    if (s === 'jcr') return 'JCR';
    if (s === 'scopus') return 'SCOPUS';
    if (s === 'kci') return 'KCI';
    return String(t).toUpperCase();
  };

  // 연도가 변경될 때 백그라운드 색을 바꿔줌
  let prevYear = null;
  let toggle = false;

  return (
    <>
      <ul className="not_print category-ranking-print-hide flex flex-col gap-y-[8px] mb-4 p-6 pl-[34px] bg-[#F3F5F9] text-[14px] text-[#6C7687] font-medium rounded-xl list-disc lg:mb-6">
        {!rmVars.isLogin && (
          <li>
            <img src={`${rmVars.themeUrl}images/warning-ico-6C7687.png`} className="not_print absolute top-[25px] left-6 w-[17px]" alt="error" />
            <p className="font-bold">Category Rankings are available after <a href={getLoginUrlWithRedirect()} className="text-[#4B4DFF]">login</a>.</p>
            <div>If you are not logged in, some values will be masked for access control purposes.</div>
          </li>
        )}
        <li>
          카테고리별 [Top %] 산출식 : (해당 저널 카테고리별 순위/해당 저널 카테고리별 총 저널 수)X100
        </li>
        <li>
          카테고리별 순위, 저널 수 기준 : JCR(Rank by Journal Impact Factor), Scopus(CiteScore rank), KCI(학술지인용정보의 KCI IF (2년))
        </li>
        <li>
          * 저널 수(Total), 저널 랭킹(Ranking), 상위%(Top %)의 괄호 안에 있는 값은 ESCI 저널을 제외한 SCIE, SSCI, AHCI 저널의 랭킹을 계산한 수치입니다.
        </li>
      </ul>
      <div className="print-hide-tabs">
        {tabs.length > 0 && (
          <ul id="customOptions" className="not_print mb-4 p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] inline-flex text-[15px] gap-[5px]">
            {tabs.map(t => (
              <li
                key={t}
                className={(activeTab === t ? 'active ' : '') + 'w-[104px] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer'}
                onClick={() => setActiveTab(t)}
                data-value={t}
              >
                {typeLabel(t)}
              </li>
            ))}
          </ul>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr>
                <th className="w-[50px]">No.</th>
                <th className="w-[50px]">Year</th>
                <th>Category Name</th>
                <th className="w-[80px]">Total</th>
                <th className="w-[70px]">Ranking</th>
                <th className="w-[60px]">Top %</th>
                <th className="w-[80px]">Percentile</th>
                <th className="w-[60px]">Quartile</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-[14px] text-[#6C7687]">
                    No category ranking data for this DB.
                  </td>
                </tr>
              ) : (
                sorted.map((r, idx) => {
                  const isJcr = String(r?.indicator_type || '').toLowerCase() === 'jcr';

                  const year = r?.indicator_year ?? null;

                  // year가 바뀌는 시점에만 토글
                  if (prevYear !== null && year !== prevYear) {
                    toggle = !toggle;
                  }
                  prevYear = year;

                  const rowClassName = toggle ? 'bg-[#F9F9F9]' : 'bg-white';

                  return (
                    <tr key={idx} className={rowClassName}>
                      <td>{idx + 1}</td>
                      <td>{r?.indicator_year ?? ''}</td>
                      <td className="text-left">{categoryLabel(r?.indicator_type, r?.edition, r?.category_name || r?.ranking_type)}</td>
                      <td>{isJcr ? renderMainAndAlt(r?.category_count, r?.category_count2) : fmtCount(r?.category_count)}</td>
                      <td>{isJcr ? renderMainAndAlt(r?.ranking, r?.ranking2) : fmtCount(r?.ranking)}</td>
                      <td>{isJcr ? renderPercentWithAlt(r?.percent, r?.percent2) : fmtPercent(r?.percent)}</td>
                      <td>{r?.percentile ?? ''}</td>
                      <td>{r?.quartile ?? ''}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="print-all-tabs" style={{ display: 'none' }}>
        {tabs.map(tabKey => {
          const tabRows = list.filter(r => String(r.indicator_type).toLowerCase() === tabKey);
          const tabSorted = [...tabRows].sort((a, b) => {
            const ya = Number(a?.indicator_year || 0);
            const yb = Number(b?.indicator_year || 0);
            if (yb !== ya) return yb - ya;
            const ra = Number(a?.ranking || 0);
            const rb = Number(b?.ranking || 0);
            if (ra !== rb) return ra - rb;
            return String(a?.category_name || '').localeCompare(String(b?.category_name || ''));
          });
          let printPrevYear = null;
          let printToggle = false;
          return (
            <div key={tabKey} className={`${tabKey !== tabs[0] ? 'not_print ' : ''}mb-6`}>
              <h4 className="text-[16px] font-bold mb-3">{typeLabel(tabKey)}</h4>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr>
                      <th className="w-[50px]">No.</th>
                      <th className="w-[50px]">Year</th>
                      <th>Category Name</th>
                      <th className="w-[80px]">Total</th>
                      <th className="w-[70px]">Ranking</th>
                      <th className="w-[60px]">Top %</th>
                      <th className="w-[80px]">Percentile</th>
                      <th className="w-[60px]">Quartile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabSorted.map((r, idx) => {
                      const isJcr = String(r?.indicator_type || '').toLowerCase() === 'jcr';
                      const year = r?.indicator_year ?? null;
                      if (printPrevYear !== null && year !== printPrevYear) {
                        printToggle = !printToggle;
                      }
                      printPrevYear = year;
                      const rowClassName = printToggle ? 'bg-[#F9F9F9]' : 'bg-white';
                      return (
                        <tr key={idx} className={rowClassName}>
                          <td>{idx + 1}</td>
                          <td>{r?.indicator_year ?? ''}</td>
                          <td className="text-left">{categoryLabel(r?.indicator_type, r?.edition, r?.category_name || r?.ranking_type)}</td>
                          <td>{isJcr ? renderMainAndAlt(r?.category_count, r?.category_count2) : fmtCount(r?.category_count)}</td>
                          <td>{isJcr ? renderMainAndAlt(r?.ranking, r?.ranking2) : fmtCount(r?.ranking)}</td>
                          <td>{isJcr ? renderPercentWithAlt(r?.percent, r?.percent2) : fmtPercent(r?.percent)}</td>
                          <td>{r?.percentile ?? ''}</td>
                          <td>{r?.quartile ?? ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
